
import React, { useState } from 'react';
import { X, Mail, Lock, Loader2, ArrowLeft, CheckCircle2, AlertTriangle, Smartphone } from 'lucide-react';
import { supabase } from '../supabase';
import { Logo } from './Logo';

interface AuthModalProps {
  onClose: () => void;
  onSuccess: (user: any) => void;
}

type AuthView = 'login' | 'signup' | 'reset';

export const AuthModal: React.FC<AuthModalProps> = ({ onClose, onSuccess }) => {
  const [view, setView] = useState<AuthView>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<{ message: string; type?: 'error' | 'warning' } | null>(null);
  const [successView, setSuccessView] = useState<'signup' | 'reset' | null>(null);

  const getErrorMessage = (err: any) => {
    const msg = err.message?.toLowerCase() || '';
    if (msg.includes('rate limit')) return "Too many requests. Please wait a few minutes before trying again.";
    if (msg.includes('invalid login credentials')) return "Incorrect email or password. Please try again.";
    if (msg.includes('email not confirmed')) return "Please check your inbox and confirm your email before logging in.";
    if (msg.includes('already registered')) return "This email is already registered. Try logging in instead.";
    return err.message || "An unexpected error occurred. Please check your connection.";
  };

  const handleGoogleLogin = async () => {
    if (!supabase) return;
    setLoading(true);
    try {
      const { error: authError } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin,
        },
      });
      if (authError) throw authError;
    } catch (err: any) {
      setError({ message: getErrorMessage(err) });
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabase) return setError({ message: "Auth service unavailable." });
    setError(null);
    setLoading(true);

    try {
      if (view === 'login') {
        const { data, error: authError } = await supabase.auth.signInWithPassword({ email, password });
        if (authError) throw authError;
        if (data.user) { onSuccess(data.user); onClose(); }
      } else if (view === 'signup') {
        const { data, error: authError } = await supabase.auth.signUp({ 
          email, 
          password, 
          options: { 
            emailRedirectTo: window.location.origin,
            data: {
              phone: phone,
              full_name: email.split('@')[0]
            }
          } 
        });
        if (authError) throw authError;
        if (data.user && !data.session) setSuccessView('signup');
        else if (data.user && data.session) { onSuccess(data.user); onClose(); }
      } else if (view === 'reset') {
        const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/?view=reset-password`,
        });
        if (resetError) throw resetError;
        setSuccessView('reset');
      }
    } catch (err: any) { setError({ message: getErrorMessage(err) }); } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md animate-in fade-in duration-300" onClick={onClose}>
      <div className="bg-white dark:bg-zinc-950 w-full max-w-[400px] rounded-[32px] overflow-y-auto max-h-[90vh] shadow-2xl relative scrollbar-hide" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 p-3 text-gray-400 hover:text-gray-900 dark:hover:text-white bg-gray-100 dark:bg-zinc-900 rounded-full transition-all active:scale-90 z-10"><X className="w-5 h-5" /></button>

        <div className="p-8 md:p-10">
          <div className="text-center mb-8 flex flex-col items-center">
            <Logo size={48} className="mb-6" />
            <h2 className="text-3xl font-black tracking-tighter text-black dark:text-white mb-2 uppercase">
              {successView ? 'Check Your Email' : view === 'login' ? 'Welcome Back' : view === 'signup' ? 'Join Findily' : 'Reset Password'}
            </h2>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em]">
              {successView ? 'Next steps sent to your inbox' : view === 'login' ? 'Access your professional dashboard' : view === 'signup' ? 'Start your elite catalog today' : 'Enter email to recover access'}
            </p>
          </div>

          {!successView && (
            <div className="mb-6 space-y-3">
              <button 
                onClick={handleGoogleLogin}
                disabled={loading}
                className="w-full py-4 bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 text-gray-900 dark:text-white rounded-2xl font-black text-[10px] uppercase tracking-widest flex items-center justify-center gap-3 active:scale-[0.98] transition-all"
              >
                <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" className="w-4 h-4" alt="Google" />
                Continue with Google
              </button>
              
              <div className="flex items-center gap-4 py-2">
                <div className="flex-1 h-[1px] bg-gray-100 dark:bg-zinc-900"></div>
                <span className="text-[9px] font-black text-gray-300 uppercase tracking-widest">or email</span>
                <div className="flex-1 h-[1px] bg-gray-100 dark:bg-zinc-900"></div>
              </div>
            </div>
          )}

          {error && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/20 rounded-2xl flex items-start gap-3">
              <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
              <p className="text-[11px] font-bold text-red-600 dark:text-red-400 uppercase tracking-wider leading-relaxed">{error.message}</p>
            </div>
          )}

          {successView ? (
            <div className="text-center space-y-6 py-4 animate-in zoom-in-95 duration-300">
              <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mx-auto"><CheckCircle2 className="w-10 h-10 text-green-500" /></div>
              <div className="space-y-2">
                <p className="text-sm font-black dark:text-white uppercase tracking-tight">Success</p>
                <p className="text-xs text-gray-500 dark:text-zinc-500 font-medium leading-relaxed">Verification link sent to {email}.</p>
              </div>
              <button onClick={() => { setSuccessView(null); setView('login'); }} className="w-full py-4 bg-black dark:bg-white text-white dark:text-black rounded-2xl font-black text-xs uppercase tracking-widest active:scale-95 transition-all">Return to Login</button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Email</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input required type="email" placeholder="agent@findily.com" className="w-full pl-12 pr-4 py-4 bg-gray-50 dark:bg-zinc-900 dark:text-white rounded-2xl text-sm font-bold outline-none border-2 border-transparent focus:border-blue-500 transition-all" value={email} onChange={e => setEmail(e.target.value)} disabled={loading} />
                </div>
              </div>

              {view === 'signup' && (
                <div className="space-y-1.5 animate-in slide-in-from-top-2 duration-300">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Phone Number</label>
                  <div className="relative">
                    <Smartphone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input required type="tel" placeholder="e.g. 2348012345678" className="w-full pl-12 pr-4 py-4 bg-gray-50 dark:bg-zinc-900 dark:text-white rounded-2xl text-sm font-bold outline-none border-2 border-transparent focus:border-blue-500 transition-all" value={phone} onChange={e => setPhone(e.target.value)} disabled={loading} />
                  </div>
                </div>
              )}

              {view !== 'reset' && (
                <div className="space-y-1.5">
                  <div className="flex justify-between items-center ml-1">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Password</label>
                    {view === 'login' && <button type="button" onClick={() => setView('reset')} className="text-[10px] font-black text-blue-500 uppercase tracking-widest hover:underline">Forgot?</button>}
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input required type="password" placeholder="••••••••" className="w-full pl-12 pr-4 py-4 bg-gray-50 dark:bg-zinc-900 dark:text-white rounded-2xl text-sm font-bold outline-none border-2 border-transparent focus:border-blue-500 transition-all" value={password} onChange={e => setPassword(e.target.value)} disabled={loading} />
                  </div>
                </div>
              )}
              <button type="submit" disabled={loading} className="w-full py-5 bg-blue-600 text-white rounded-2xl font-black text-xs uppercase tracking-[0.2em] hover:opacity-90 active:scale-[0.98] transition-all shadow-xl shadow-blue-500/20 mt-4 flex items-center justify-center gap-2">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : view === 'login' ? 'Login' : view === 'signup' ? 'Create Account' : 'Recover'}
              </button>
            </form>
          )}

          {!successView && (
            <div className="mt-10 pt-6 border-t border-gray-100 dark:border-zinc-900 text-center">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">{view === 'login' ? "New to Findily?" : "Already an agent?"}</p>
              <button onClick={() => { setView(view === 'login' ? 'signup' : 'login'); setError(null); }} disabled={loading} className="w-full py-3 border-2 border-gray-100 dark:border-zinc-800 text-gray-900 dark:text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-gray-50 dark:hover:bg-zinc-900 transition-all active:scale-95">
                {view === 'login' ? "Register Now" : "Sign In"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
